import { type RefObject, useEffect } from "react";

/**
 * F-215: an open dropdown or popover closes on a click outside it (or its
 * trigger) and on Esc. `onClose(true)` means focus should return to the
 * trigger (the Esc path).
 */
export function useShellDismiss(args: {
  open: boolean;
  onClose: (restoreFocus: boolean) => void;
  refs: ReadonlyArray<RefObject<HTMLElement | null>>;
}) {
  const { onClose, open, refs } = args;

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (refs.some((ref) => ref.current?.contains(target))) return;
      onClose(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        onClose(true);
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, open, refs]);
}
